import * as d3 from 'd3';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  TransitionEventEmitter,
  type ChangeEndEvent,
  type ChangeProgressEvent,
  type ChangeStartEvent,
} from '../transitionEventEmitter';

export const useInteraction = () => {
  const projectionRef = useRef(d3.geoOrthographic());
  // Animation state for smooth transitions
  const animationRef = useRef<number | null>(null);
  const transitionStateRef = useRef<{
    fromRotation: [number, number, number];
    toRotation: [number, number, number];
    fromScale: number;
    toScale: number;
    startTime: number;
    duration: number;
  } | null>(null);

  // Create event emitter instance
  const transitionEventEmitter = useMemo(
    () => new TransitionEventEmitter(),
    [],
  );
  // Event listener convenience hooks

  const onChangeStart = useCallback(
    (listener: (event: CustomEvent<ChangeStartEvent>) => void) =>
      transitionEventEmitter.addChangeStartListener(listener),
    [transitionEventEmitter],
  );
  const onChangeProgress = useCallback(
    (listener: (event: CustomEvent<ChangeProgressEvent>) => void) =>
      transitionEventEmitter.addChangeProgressListener(listener),
    [transitionEventEmitter],
  );
  const onChangeEnd = useCallback(
    (listener: (event: CustomEvent<ChangeEndEvent>) => void) =>
      transitionEventEmitter.addChangeEndListener(listener),
    [transitionEventEmitter],
  );

  // Cleanup animation on unmount
  useEffect(
    () => () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);

        // Emit cancel event on unmount
        transitionEventEmitter.emit({
          type: 'change-cancel',
          reason: 'component-unmount',
          timestamp: Date.now(),
        });
      }

      // Clear all event listeners
      transitionEventEmitter.clear();
    },
    [transitionEventEmitter],
  );
  // Animation function for smooth transitions
  const animateProjectionTransition = useCallback(() => {
    if (!transitionStateRef.current) {
      return;
    }

    const {
      fromRotation,
      toRotation,
      fromScale,
      toScale,
      startTime,
      duration,
    } = transitionStateRef.current;
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Use easeInOutCubic for smooth transition
    const easedProgress =
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    // Interpolate rotation
    const currentRotation: [number, number, number] = [
      fromRotation[0] + (toRotation[0] - fromRotation[0]) * easedProgress,
      fromRotation[1] + (toRotation[1] - fromRotation[1]) * easedProgress,
      fromRotation[2] + (toRotation[2] - fromRotation[2]) * easedProgress,
    ];

    // Interpolate scale
    const currentScale = fromScale + (toScale - fromScale) * easedProgress;

    // Apply interpolated values to projection
    projectionRef.current.rotate(currentRotation).scale(currentScale);

    // Emit progress event

    transitionEventEmitter.emit({
      type: 'change-progress',
      rotation: currentRotation,
      scale: currentScale,
      timestamp: Date.now(),
    });

    if (progress < 1) {
      // Continue animation - self-reference is intentional for recursive animation frames
      // eslint-disable-next-line react-hooks/immutability
      animationRef.current = requestAnimationFrame(animateProjectionTransition);
    } else {
      // Animation complete
      animationRef.current = null;
      transitionStateRef.current = null;
      // Emit end event

      transitionEventEmitter.emit({
        type: 'change-end',
        rotation: currentRotation,
        scale: currentScale,
        timestamp: Date.now(),
      });
    }
  }, [transitionEventEmitter]);

  // Function to start a smooth transition
  const startProjectionTransition = useCallback(
    (
      projection: d3.GeoProjection,
      toRotation: [number, number, number],
      toScale: number,
      duration: number = 1000,
    ) => {
      // Cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);

        // Emit cancel event for previous transition
        transitionEventEmitter.emit({
          type: 'change-cancel',
          reason: 'new-transition',
          timestamp: Date.now(),
        });
      }

      const fromRotation = projection.rotate();
      const fromScale = projection.scale();

      transitionStateRef.current = {
        fromRotation,
        toRotation,
        fromScale,
        toScale,
        startTime: Date.now(),
        duration,
      };
      // Emit start event
      transitionEventEmitter.emit({
        type: 'change-start',
        rotation: fromRotation,
        scale: fromScale,
        timestamp: Date.now(),
      });

      animationRef.current = requestAnimationFrame(animateProjectionTransition);
    },
    [animateProjectionTransition, transitionEventEmitter],
  );

  return {
    onChangeStart,
    onChangeProgress,
    onChangeEnd,
    projectionRef,
    animationRef,
    transitionStateRef,
    transitionEventEmitter,
    startProjectionTransition,
  };
};
